import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Banner, Button, Card, CardContent, ConfirmDialog, toast } from "bizpandit-ui";
import { apiErrorMessage } from "@/api/apiError";
import { RETURN_KEY, calendarSyncService, type CalendarProviderKey } from "./calendarSync.service";

const say = (m: string) => { try { toast.success(m); } catch { /* shown on screen */ } };
const when = (d: string | null) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : null);

/**
 * Connect your own Google Calendar or Microsoft 365 (E14, E-45) — the same
 * card for staff (HR → Calendar) and members (portal Account settings). Two-way:
 * what BizPandit books goes to your calendar, changes you make there come
 * back, and when both changed your calendar's version wins. Optional, for ever.
 */
export function CalendarConnectionsCard({ returnTo }: { returnTo: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["me-calendar"], queryFn: () => calendarSyncService.mine() });
  const [error, setError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState<CalendarProviderKey | null>(null);
  const refresh = (m?: string) => { qc.invalidateQueries({ queryKey: ["me-calendar"] }); qc.invalidateQueries({ queryKey: ["hr", "calendar"] }); setError(null); if (m) say(m); };
  const connect = useMutation({
    mutationFn: (p: CalendarProviderKey) => calendarSyncService.connect(p),
    onSuccess: (r) => { try { sessionStorage.setItem(RETURN_KEY, returnTo); } catch { /* the return page falls back */ } window.location.href = r.url; },
    onError: (e) => setError(apiErrorMessage(e, "The connection could not be started.")),
  });
  const disconnect = useMutation({ mutationFn: (p: CalendarProviderKey) => calendarSyncService.disconnect(p), onSuccess: () => { setLeaving(null); refresh("Disconnected. Your BizPandit calendar is unchanged."); }, onError: (e) => setError(apiErrorMessage(e, "Could not disconnect.")) });
  const syncNow = useMutation({ mutationFn: (p: CalendarProviderKey) => calendarSyncService.syncNow(p), onSuccess: () => refresh("Checking your calendar now — changes arrive within a minute."), onError: (e) => setError(apiErrorMessage(e, "That did not go through.")) });
  const d = q.data;
  if (!d) return q.error ? <Banner tone="danger">{apiErrorMessage(q.error, "Calendar connections could not be loaded.")}</Banner> : null;
  const label = d.connections.find((c) => c.provider === leaving)?.label ?? "";
  return (
    <Card className="border border-linegray rounded-[14px]"><CardContent className="space-y-3 p-4">
      <div>
        <p className="m-0 text-[13px] font-semibold text-navy">Your own calendar</p>
        <p className="m-0 mt-1 text-[12px] text-navy2/70">{d.note}</p>
      </div>
      {error && <Banner tone="danger">{error}</Banner>}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3">
        {d.connections.map((c) => (
          <div key={c.provider} className="rounded-[10px] border border-linegray p-3">
            <p className="m-0 text-[13px] font-medium text-navy">{c.label}</p>
            {!c.configured ? (
              <p className="m-0 mt-1 text-[11.5px] text-navy2/60">Not available yet — BizPandit has not set up {c.label} on this server.</p>
            ) : c.status === "CONNECTED" ? (
              <>
                <p className="m-0 mt-1 text-[11.5px] text-navy2/70">Connected{c.accountEmail ? ` · ${c.accountEmail}` : ""}{c.lastSyncedAt ? ` · last checked ${when(c.lastSyncedAt)}` : ""}</p>
                {c.lastError && <p className="m-0 mt-1 text-[11px] text-red-700">{c.lastError}</p>}
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" loading={syncNow.isPending} onClick={() => syncNow.mutate(c.provider)}>Sync now</Button>
                  <Button size="sm" variant="outline" onClick={() => setLeaving(c.provider)}>Disconnect</Button>
                </div>
              </>
            ) : (
              <>
                {c.lastError && <p className="m-0 mt-1 text-[11px] text-red-700">{c.lastError}</p>}
                <Button size="sm" className="mt-2" loading={connect.isPending} onClick={() => connect.mutate(c.provider)}>Connect {c.provider === "GOOGLE" ? "Google" : "Microsoft 365"}</Button>
              </>
            )}
          </div>
        ))}
      </div>
      <p className="m-0 text-[11px] text-navy2/55">Meetings you create in your own calendar show here only as private busy time — never their title or who they are with, and never to anyone else.</p>
      <ConfirmDialog open={!!leaving} onOpenChange={(o) => { if (!o) setLeaving(null); }} title={`Disconnect ${label}?`}
        description="BizPandit stops sending to and reading from it. Your BizPandit calendar stays exactly as it is; copies already in your own calendar stay there, and the busy time imported from it goes."
        confirmLabel="Disconnect" onConfirm={() => leaving && disconnect.mutate(leaving)} />
    </CardContent></Card>
  );
}
